import { PayloadAction, createSlice } from "@reduxjs/toolkit";

export type MessageWithId =
  | (UserMessage & { id: string })
  | (AgentMessage & { id: string })
  | (ToolCallMessage & { id: string })
  | (ToolResultMessage & { id: string })
  | (ErrorMessage & { id: string })
  | (SystemInitiationMessage & { id: string });

import {
  AgentMessage,
  ErrorMessage,
  SystemInitiationMessage,
  ToolCallMessage,
  ToolResultMessage,
  UserMessage,
} from "@stratify/shared";
import { RootState } from "../../../store/rootReducer";

export interface ConversationMessagesState {
  messagesByConversationId: {
    [conversationId: string]: MessageWithId[];
  };
  loadingStates: {
    [conversationId: string]: "idle" | "loading" | "succeeded" | "failed";
  };
  errorMessages: {
    [conversationId: string]: string | null;
  };
}

const initialState: ConversationMessagesState = {
  messagesByConversationId: {},
  loadingStates: {},
  errorMessages: {},
};

const conversationMessagesSlice = createSlice({
  name: "conversationMessages",
  initialState,
  reducers: {
    setMessagesLoading: (
      state,
      action: PayloadAction<{ conversationId: string }>
    ) => {
      const { conversationId } = action.payload;
      state.loadingStates[conversationId] = "loading";
      state.errorMessages[conversationId] = null;
    },
    setMessagesSuccess: (
      state,
      action: PayloadAction<{
        conversationId: string;
        messages: MessageWithId[];
      }>
    ) => {
      const { conversationId, messages } = action.payload;
      state.messagesByConversationId[conversationId] = messages;
      state.loadingStates[conversationId] = "succeeded";
      state.errorMessages[conversationId] = null;
    },
    setMessagesError: (
      state,
      action: PayloadAction<{ conversationId: string; error: string }>
    ) => {
      const { conversationId, error } = action.payload;
      state.loadingStates[conversationId] = "failed";
      state.errorMessages[conversationId] = error;
    },
  },
});

// Actions
export const { setMessagesLoading, setMessagesSuccess, setMessagesError } =
  conversationMessagesSlice.actions;

// Selectors
export const selectMessagesForConversation = (
  state: RootState,
  conversationId: string
) => state.conversationMessages.messagesByConversationId[conversationId] || [];

export const selectMessagesLoadingStatus = (
  state: RootState,
  conversationId: string
) => state.conversationMessages.loadingStates[conversationId] || "idle";

export const selectMessagesError = (state: RootState, conversationId: string) =>
  state.conversationMessages.errorMessages[conversationId] || null;

export default conversationMessagesSlice.reducer;
